import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery } from "@apollo/client";
import deviceGroupService from "../../services/deviceGroupService";
import vppCatalogService from "../../services/vppCatalogService";
import softwareUpdateService, { softwareUpdateVersionLabel } from "../../services/softwareUpdateService";
import { installProfile, removeProfile, disableProfile } from "../../commands/mdmCommands";
import { GET_CONFIG_PROFILES } from "../../queries/configProfilesQueries";
import SearchBar from "../SearchBar";
import locationService from "../../services/locationService";

function badge(ok, warn, labelOk, labelWait, labelWarn) {
  if (warn) return { className: "text-bg-warning", label: labelWarn };
  if (ok) return { className: "text-bg-success", label: labelOk };
  return { className: "text-bg-secondary", label: labelWait };
}

function candidateKey(row) {
  return row.key || row.registrationId || row._id;
}

function deviceTypeLabel(type) {
  return ({ mac: "Mac", iphone: "iPhone", ipad: "iPad", appletv: "Apple TV" })[type] || type;
}

export default function GroupMembersModal({ visible, group, onClose }) {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [candidateLocation, setCandidateLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const { data: profilesData } = useQuery(GET_CONFIG_PROFILES, { skip: !visible });
  const profileById = useMemo(() => {
    const map = {};
    for (const profile of profilesData?.configProfiles || []) map[profile._id] = profile;
    return map;
  }, [profilesData]);

  const loadMembers = useCallback(async () => {
    if (!group?._id) return;
    setLoading(true);
    try {
      const data = await deviceGroupService.getGroupMembers(group._id);
      setMembers(data.members || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Geräte konnten nicht geladen werden.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [group?._id]);

  useEffect(() => {
    if (!visible || !group) return;
    setQuery("");
    setExpanded(null);
    setShowAdd(false);
    setSelected([]);
    setCandidateQuery("");
    setCandidateLocation(group?.location?._id || group?.location || "");
    loadMembers();
  }, [visible, group, loadMembers]);

  useEffect(() => {
    if (!visible) return;
    locationService.getLocations().then(setLocations).catch(() => setLocations([]));
  }, [visible]);

  useEffect(() => {
    if (!visible || !showAdd || !group) return;
    const handle = setTimeout(async () => {
      setLoadingCandidates(true);
      try {
        const data = await deviceGroupService.getGroupCandidates(group._id, {
          q: candidateQuery,
          location: candidateLocation,
        });
        setCandidates(data.candidates || []);
      } catch {
        setCandidates([]);
      } finally {
        setLoadingCandidates(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [visible, showAdd, group, candidateQuery, candidateLocation]);

  const visibleMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((row) =>
      [row.deviceName, row.plannedDeviceName, row.serialNumber, row.assetTag, row.udid]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [members, query]);

  if (!visible || !group) return null;

  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      await fn();
      if (success) toast.success(success);
      await loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  const addSelected = async () => {
    if (!selected.length) {
      toast.error("Bitte mindestens ein Gerät wählen.");
      return;
    }
    const byKey = new Map(candidates.map((row) => [candidateKey(row), row]));
    const registrationIds = [];
    const devices = [];
    for (const key of selected) {
      const row = byKey.get(key);
      if (!row) continue;
      if (row.registrationId) {
        registrationIds.push(row.registrationId);
        continue;
      }
      if (!row.serialNumber) {
        toast.error("Gerät ohne Seriennummer kann nicht hinzugefügt werden.");
        continue;
      }
      devices.push({
        serialNumber: row.serialNumber,
        udid: row.udid || undefined,
        deviceName: row.deviceName || undefined,
        assetTag: row.assetTag || undefined,
        location: row.location?._id || row.location || undefined,
      });
    }
    await run(
      "add",
      () => deviceGroupService.addGroupMembers(group._id, { registrationIds, devices }),
      `${selected.length} Gerät(e) hinzugefügt.`
    );
    setSelected([]);
    setShowAdd(false);
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Geräte · {group.name}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="d-flex flex-wrap gap-2 justify-content-between align-items-end mb-3">
              <div style={{ minWidth: 220, flex: 1 }}>
                <SearchBar searchHandler={setQuery} value={query} placeholder="Gerät oder Seriennummer" />
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAdd((v) => !v)}>
                {showAdd ? "Auswahl schließen" : "Geräte hinzufügen"}
              </button>
            </div>

            {showAdd && (
              <div className="card p-3 mb-3">
                <p className="small text-muted mb-2">
                  Alle im MDM eingeschriebenen Geräte (DEP und User-Enrollment) sowie ausstehende Registrierungen, die dieser Gruppe noch nicht angehören.
                </p>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <input
                    className="form-control form-control-sm"
                    style={{ minWidth: 180, flex: 1 }}
                    placeholder="Suchen…"
                    value={candidateQuery}
                    onChange={(e) => setCandidateQuery(e.target.value)}
                  />
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto", minWidth: 180 }}
                    value={candidateLocation}
                    onChange={(e) => setCandidateLocation(e.target.value)}
                  >
                    <option value="">Alle Standorte</option>
                    {locations.map((loc) => (
                      <option key={loc._id} value={loc._id}>
                        {loc.schoolNumber} – {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                {loadingCandidates ? (
                  <p className="text-muted mb-2">Suche…</p>
                ) : (
                  <div style={{ maxHeight: 200, overflow: "auto" }} className="mb-2">
                    {candidates.length === 0 ? (
                      <p className="small text-muted mb-0">Keine Treffer.</p>
                    ) : (
                      candidates.map((row) => {
                        const key = candidateKey(row);
                        return (
                        <label key={key} className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected.includes(key)}
                            onChange={(e) => {
                              setSelected((prev) =>
                                e.target.checked ? [...prev, key] : prev.filter((item) => item !== key)
                              );
                            }}
                          />
                          <span className="form-check-label">
                            {row.deviceName || row.plannedDeviceName || row.serialNumber}
                            <span className="small text-muted">
                              {" "}· {row.serialNumber || row.udid}
                              {row.deviceType ? ` · ${deviceTypeLabel(row.deviceType)}` : ""}
                              {row.location?.name ? ` · ${row.location.schoolNumber ? `${row.location.schoolNumber} – ` : ""}${row.location.name}` : ""}
                              {` · ${row.enrollmentStatus === "enrolled" ? "eingeschrieben" : "ausstehend"}`}
                            </span>
                          </span>
                        </label>
                        );
                      })
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={busy === "add" || selected.length === 0}
                  onClick={addSelected}
                >
                  Ausgewählte hinzufügen ({selected.length})
                </button>
              </div>
            )}

            {loading ? (
              <p className="text-muted">Laden…</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Gerät</th>
                      <th>Status</th>
                      <th>OS-Update</th>
                      <th>Profile</th>
                      <th>Apps</th>
                      <th>DDM</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMembers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-muted">Keine Geräte in dieser Gruppe.</td>
                      </tr>
                    ) : (
                      visibleMembers.map((row) => {
                        const profilesOk = row.profiles.filter((item) => item.installed).length;
                        const appsOk = row.apps.filter((item) => item.installed).length;
                        const ddmOk = row.ddm.filter((item) => item.current && item.valid === "valid").length;
                        const enrolled = row.enrollmentStatus === "enrolled" && row.udid;
                        return (
                          <FragmentRow
                            key={row.registrationId}
                            row={row}
                            expanded={expanded === row.registrationId}
                            onToggle={() => setExpanded((prev) => (prev === row.registrationId ? null : row.registrationId))}
                            profilesOk={profilesOk}
                            appsOk={appsOk}
                            ddmOk={ddmOk}
                            enrolled={enrolled}
                            busy={busy}
                            profileById={profileById}
                            onResync={() => run(
                              `sync-${row.registrationId}`,
                              () => deviceGroupService.resyncGroupMember(group._id, row.registrationId),
                              "Erneute Installation angestoßen."
                            )}
                            onRemove={() => {
                              if (!window.confirm("Gerät aus dieser Gruppe entfernen?")) return;
                              run(
                                `rm-${row.registrationId}`,
                                () => deviceGroupService.removeGroupMember(group._id, row.registrationId),
                                "Aus der Gruppe entfernt."
                              );
                            }}
                            onDisableAll={() => {
                              if (!window.confirm("Alle Gruppen-Apps auf diesem Gerät entfernen und deaktivieren?")) return;
                              run(`dis-${row.registrationId}`, async () => {
                                for (const app of row.apps) {
                                  if (!row.udid || !app.adamId) continue;
                                  await vppCatalogService.removeDeviceApp(row.udid, app.adamId, {
                                    disable: true,
                                    bundleId: app.bundleId,
                                  });
                                }
                              }, "Apps deaktiviert.");
                            }}
                            onApp={(app, action) => {
                              if (!row.udid) return;
                              if (action === "reinstall") {
                                run(`re-${row.udid}-${app.adamId}`, () => vppCatalogService.reinstallDeviceApp(row.udid, app.adamId), "Erneute Installation angestoßen.");
                              } else if (action === "remove") {
                                run(`rm-${row.udid}-${app.adamId}`, () => vppCatalogService.removeDeviceApp(row.udid, app.adamId, {
                                  disable: false,
                                  bundleId: app.bundleId,
                                }), "Entfernen angestoßen.");
                              } else {
                                run(`dis-${row.udid}-${app.adamId}`, () => vppCatalogService.removeDeviceApp(row.udid, app.adamId, {
                                  disable: true,
                                  bundleId: app.bundleId,
                                }), "Entfernt und deaktiviert.");
                              }
                            }}
                            onProfile={(profile, action) => {
                              if (!row.udid) return;
                              const full = profileById[profile.id];
                              if (action === "reinstall") {
                                if (!full) {
                                  toast.error("Profildaten nicht geladen.");
                                  return;
                                }
                                run(`pr-${row.udid}-${profile.id}`, () => installProfile(row.udid, full), "Profil-Installation angestoßen.");
                              } else if (action === "remove") {
                                run(`prm-${row.udid}-${profile.id}`, () => removeProfile(row.udid, profile.identifier, profile.name), "Profil-Entfernen angestoßen.");
                              } else {
                                run(`pd-${row.udid}-${profile.id}`, () => disableProfile(row.udid, profile.identifier, profile.name), "Profil deaktiviert.");
                              }
                            }}
                            onResetSoftwareUpdate={() => {
                              if (!row.udid) return;
                              run(
                                `su-${row.udid}`,
                                () => softwareUpdateService.setDeviceSoftwareUpdate(row.udid, {
                                  mode: "inherit",
                                  serialNumber: row.serialNumber,
                                }),
                                "OS-Update folgt wieder der Gruppe."
                              );
                            }}
                          />
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Schließen</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FragmentRow({
  row,
  expanded,
  onToggle,
  profilesOk,
  appsOk,
  ddmOk,
  enrolled,
  busy,
  onResync,
  onRemove,
  onDisableAll,
  onApp,
  onProfile,
  onResetSoftwareUpdate,
}) {
  const suLabel = row.softwareUpdateOverride === "none"
    ? "kein Update"
    : (row.softwareUpdateVersion || (row.softwareUpdateEffective
      ? softwareUpdateVersionLabel(row.softwareUpdateEffective)
      : "—"));
  return (
    <>
      <tr>
        <td>
          {row.path ? <Link to={row.path}>{row.deviceName || row.serialNumber}</Link> : (row.deviceName || row.serialNumber)}
          <div className="small text-muted">
            <code>{row.serialNumber}</code>
            {row.assetTag ? ` · ${row.assetTag}` : ""}
          </div>
        </td>
        <td>
          <span className={`badge ${row.enrollmentStatus === "enrolled" ? "text-bg-success" : "text-bg-secondary"}`}>
            {row.enrollmentStatus || "—"}
          </span>
        </td>
        <td>
          {suLabel}
          {row.softwareUpdateOverridden ? (
            <span className="badge text-bg-warning ms-1">Override</span>
          ) : null}
        </td>
        <td>{row.profiles.length ? `${profilesOk}/${row.profiles.length}` : "—"}</td>
        <td>{row.apps.length ? `${appsOk}/${row.apps.length}` : "—"}</td>
        <td>{row.ddm.length ? `${ddmOk}/${row.ddm.length}` : "—"}</td>
        <td className="text-nowrap">
          <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={onToggle}>
            {expanded ? "Zuklappen" : "Details"}
          </button>
          {enrolled && (
            <button type="button" className="btn btn-sm btn-outline-primary me-1" disabled={Boolean(busy)} onClick={onResync}>
              Erneut installieren
            </button>
          )}
          {enrolled && row.apps.length > 0 && (
            <button type="button" className="btn btn-sm btn-outline-warning me-1" disabled={Boolean(busy)} onClick={onDisableAll}>
              Deaktivieren
            </button>
          )}
          <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(busy)} onClick={onRemove}>
            Entfernen
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7}>
            <div className="small">
              <strong>OS-Update</strong>
              <p className="mb-2">
                {suLabel}
                {row.softwareUpdateOverridden ? " · Geräte-Override" : " · Gruppenvorgabe"}
                {enrolled && row.softwareUpdateOverridden && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-2"
                    disabled={Boolean(busy)}
                    onClick={onResetSoftwareUpdate}
                  >
                    Override zurücksetzen
                  </button>
                )}
              </p>
              <strong>Profile</strong>
              {row.profiles.length === 0 ? <p className="text-muted mb-2">Keine.</p> : (
                <ul className="mb-3">
                  {row.profiles.map((profile) => {
                    const st = badge(profile.installed, profile.disabled, "installiert", "offen", "deaktiviert");
                    return (
                      <li key={profile.id} className="mb-1">
                        {profile.name}{" "}
                        <span className={`badge ${st.className}`}>{st.label}</span>
                        {enrolled && (
                          <span className="ms-2">
                            <button type="button" className="btn btn-sm btn-outline-primary me-1" disabled={Boolean(busy)} onClick={() => onProfile(profile, "reinstall")}>Erneut installieren</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-1" disabled={Boolean(busy)} onClick={() => onProfile(profile, "remove")}>Entfernen</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(busy)} onClick={() => onProfile(profile, "disable")}>Deaktivieren</button>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <strong>Apps</strong>
              {row.apps.length === 0 ? <p className="text-muted mb-2">Keine.</p> : (
                <ul className="mb-3">
                  {row.apps.map((app) => {
                    const st = badge(app.installed, app.disabled, "installiert", "offen", "deaktiviert");
                    return (
                      <li key={app.adamId} className="mb-1">
                        {app.name}{" "}
                        <span className={`badge ${st.className}`}>{st.label}</span>
                        {enrolled && (
                          <span className="ms-2">
                            <button type="button" className="btn btn-sm btn-outline-primary me-1" disabled={Boolean(busy)} onClick={() => onApp(app, "reinstall")}>Erneut installieren</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-1" disabled={Boolean(busy)} onClick={() => onApp(app, "remove")}>Entfernen</button>
                            <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(busy) || app.disabled} onClick={() => onApp(app, "disable")}>Deaktivieren</button>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <strong>DDM</strong>
              {row.ddm.length === 0 ? <p className="text-muted mb-0">Keine Deklarationen.</p> : (
                <ul className="mb-0">
                  {row.ddm.map((item) => {
                    const ok = item.current && String(item.valid).toLowerCase() === "valid";
                    const st = badge(ok, String(item.valid).toLowerCase() === "invalid", "synchron", "offen", "Fehler");
                    return (
                      <li key={item.identifier}>
                        <code>{item.identifier}</code>{" "}
                        <span className={`badge ${st.className}`}>{st.label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
